import { useTranslate } from 'react-admin';
import { Alert, Button, Stack } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { VAULTS_NS as NS, VAULTS_PERMISSION_RESOURCE as RESOURCE } from './constants';
import { ContactDialog } from './ContactDialog';
import { useVaultContact } from './useVaultContact';
import { useVaultOrder } from './useVaultOrder';
import { useVaults } from './useVaults';
import { VaultsTable } from './VaultsTable';

const VaultsList = () => {
  const translate = useTranslate();
  const { vaults, loading, loadError, applicationsNotice, refetch } = useVaults();
  // XBIT-4401: the contact edit; the list is read again after it.
  const contact = useVaultContact({ onSettled: refetch });
  // XBIT-4247: the list in the order operations set, and the drop that changes it.
  const order = useVaultOrder({ vaults, refetch });

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refetch}
              disabled={loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Stack spacing={3} sx={{ mt: 3 }}>
          <Alert severity="info">{translate(`${NS}.snapshotHint`)}</Alert>
          <PermissionButton resource={RESOURCE} action="reorder">
            <Alert severity="info">{translate(`${NS}.order.hint`)}</Alert>
          </PermissionButton>
          {loadError && <Alert severity="error">{loadError}</Alert>}
          {applicationsNotice && <Alert severity="warning">{applicationsNotice}</Alert>}
          <LoadingPanel isLoading={loading}>
            <VaultsTable
              vaults={order.vaults}
              onEditContact={(v) => contact.open({ vaultId: v.vaultId, name: v.name })}
              onReorder={order.drop}
              canReorder={order.canReorder}
            />
          </LoadingPanel>
        </Stack>
        <ContactDialog form={contact} />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default VaultsList;
